// src/components/DashboardTabs/CalendarTab.jsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";
import { CalendarPlus, Download } from "lucide-react";

// ---------------- Kalenteri (dashboard-värit, modal vain dashboardissa) -----------------

// Kevyt välimuisti kalenterille (1 min TTL)
const AV_CACHE = new Map();   // availability
const CAL_CACHE = new Map();  // busy (booked/pending/blackout)
const CACHE_TTL_MS = 60 * 1000;

const CAL_THEME = {
  openBg:      "bg-lime-400/90",
  openRing:    "ring-black/20",
  closedHover: "hover:bg-black/5 dark:hover:bg-white/5",
  legendOpen:  "bg-emerald-400/30 ring-emerald-600/40",
  todayBadge:  "bg-fuchsia-500/80 text-black dark:text-white",
  pastDim:     "opacity-20",
  bookedBg:    "bg-red-500/30 ring-1 ring-red-600/40",
  pendingBg:   "bg-yellow-400/30 ring-1 ring-yellow-600/40",
  blackoutBg:  "bg-black/10 ring-1 ring-black/10 dark:bg-white/10 dark:ring-white/10",
};



function pad2(n){ return n < 10 ? `0${n}` : `${n}`; }
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;

const addDays   = (d,n)=>{ const x=new Date(d); x.setDate(x.getDate()+n); return x; };
const addMonths = (d,n)=>{ const x=new Date(d); x.setMonth(x.getMonth()+n); x.setDate(1); x.setHours(0,0,0,0); return x; };
const sameMonth = (a,b)=> a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth();

// --- FI date format helpers ---
function formatFiShort(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("fi-FI", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function formatFiLong(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("fi-FI", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// --- FI-statuskalvo Google/ICS-tekstiin ---
function fiStatusLine(modal, holdTtlHours = 1) {
  const st = (modal?.data?.status || "").toLowerCase();
  const h = Math.max(1, Number(holdTtlHours) || 1);
  const unit = h === 1 ? "1 tunti" : `${h} tuntia`;

  if (st === "booked") return "Tila: varattu";
  if (st === "pending") {
    // Jos backendi antaa expires_at, näytetään myös kellonaika (valinnainen)
    const ex = modal?.data?.expires_at ? new Date(modal.data.expires_at) : null;
    const until =
      ex && !Number.isNaN(ex.getTime())
        ? ` (asti ${ex.toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" })})`
        : "";
    return `Tila: odottaa (pallo asiakkaalla, pidossa ${unit}${until})`;
  }
  if (st === "blackout") return "Tila: ei varattavissa";
  if (st === "completed") return "Tila: toteutunut";
  return st ? `Tila: ${st}` : "";
}


// €-apuri
const EUR = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "–"
    : `${Number(n).toFixed(2).replace(/\.00$/,"")} €`;

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">{label}</span>
      <span className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-medium text-right">{String(value ?? "–")}</span>
    </div>
  );
}
Row.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.any };

// --------- Kalenteriapurit (Google linkki & ICS) ---------

// Pieni apuri: YYYYMMDD
function yyyymmdd(dateIso) {
  // dateIso on muotoa "YYYY-MM-DD"
  return String(dateIso).replaceAll("-", "");
}

// Lisää päiviä "YYYY-MM-DD" -merkkijonoon ja palauta YYYYMMDD
function addDaysISO(iso, days) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

// Escapet ICS-kenttiin (,\; ja rivinvaihdot)
function icsEscape(s = "") {
  return String(s)
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

// UTC timestamp ICS:ään
function icsTimestampUTC(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

/**
 * Rakenna Google Calendar -linkki.
 * - Jos kellonaikoja ei ole, tehdään koko päivän tapahtuma (all-day).
 * - Jos ajat on annettu (startTime/endTime “HH:mm”), ne viedään UTC:na.
 */
function buildGoogleLink({
  title,
  date,        // "YYYY-MM-DD"
  endDate,     // "YYYY-MM-DD" tai undefined → all-day yksipäiväinen
  startTime,   // "HH:mm" (valinnainen)
  endTime,     // "HH:mm" (valinnainen)
  timeZone,    // esim. "Europe/Helsinki" (valinnainen – käytetään vain kuvaustekstissä)
  location,
  details
}) {
  let datesParam = "";

  if (!startTime || !endTime) {
    // All-day
    const start = yyyymmdd(date);
    // Google vaatii all-daylle päättymispäivän eksklusiivisena:
    const endExclusive = endDate ? addDaysISO(endDate, 1) : addDaysISO(date, 1);
    datesParam = `${start}/${endExclusive}`;
  } else {
    // Kellonajallinen → muutetaan UTC:ksi ja lisätään Z
    const start = new Date(`${date}T${startTime}:00${Intl.DateTimeFormat().resolvedOptions().timeZone ? "" : ""}`);
    const end = new Date(`${(endDate || date)}T${endTime}:00${Intl.DateTimeFormat().resolvedOptions().timeZone ? "" : ""}`);
    // Huom: selain tulkitsee yllä olevan paikalliseen aikavyöhykkeeseen.
    // Tehdään Googlelle UTC-muoto (YYYYMMDDTHHMMSSZ)
    const toG = (dt) => {
      const pad = (n) => String(n).padStart(2, "0");
      return (
        dt.getUTCFullYear() +
        pad(dt.getUTCMonth() + 1) +
        pad(dt.getUTCDate()) +
        "T" +
        pad(dt.getUTCHours()) +
        pad(dt.getUTCMinutes()) +
        pad(dt.getUTCSeconds()) +
        "Z"
      );
    };
    datesParam = `${toG(start)}/${toG(end)}`;
  }

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "Keikka",
    dates: datesParam,
    details: details || (timeZone ? `Aikavyöhyke: ${timeZone}` : ""),
    location: location || ""
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Rakenna ICS-tiedosto (all-day oletuksena).
 * Jos startTime/endTime on annettu, käytetään kellonajallista tapahtumaa (UTC).
 */
function buildICS({
  uid,
  title,
  date,        // "YYYY-MM-DD"
  endDate,     // "YYYY-MM-DD" | undefined
  startTime,   // "HH:mm" | undefined
  endTime,     // "HH:mm" | undefined
  location,
  description,
}) {
  const nowStamp = icsTimestampUTC(new Date());
  const summary = icsEscape(title || "Keikka");
  const loc = icsEscape(location || "");
  const desc = icsEscape(description || "");

  let dtStart = "";
  let dtEnd = "";

  if (!startTime || !endTime) {
    // All-day
    const startYmd = yyyymmdd(date);
    // ICS:ssä all-dayn DTEND on eksklusiivinen
    const endExcl = yyyymmdd(endDate || date); 
    const endNext = addDaysISO(endExcl.slice(0,4)+"-"+endExcl.slice(4,6)+"-"+endExcl.slice(6,8), 1);
    dtStart = `DTSTART;VALUE=DATE:${startYmd}`;
    dtEnd = `DTEND;VALUE=DATE:${endNext}`;
  } else {
    // Kellonajallinen → UTC
    const start = new Date(`${date}T${startTime}:00`);
    const end = new Date(`${(endDate || date)}T${endTime}:00`);
    const toUTC = (dt) => icsTimestampUTC(dt).replace(/Z$/, "Z");
    dtStart = `DTSTART:${toUTC(start)}`;
    dtEnd = `DTEND:${toUTC(end)}`;
  }

  const uidStr = icsEscape(uid || `buukkeri-${date}-${Math.random().toString(36).slice(2)}@buukkeri`);

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Buukkeri//Dashboard//FI",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uidStr}`,
    `DTSTAMP:${nowStamp}`,
    dtStart,
    dtEnd,
    `SUMMARY:${summary}`,
    `LOCATION:${loc}`,
    `DESCRIPTION:${desc}`,
    "END:VEVENT",
    "END:VCALENDAR",
    "" // loppuun CRLF viimeiseen riviin
  ].join("\r\n");
}

// Lataa tekstitiedosto (.ics) käyttäjälle
function downloadICS(filename, icsText) {
  const blob = new Blob([icsText], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


export default function CalendarTab({ band, isDashboard = true, holdTtlHours = 1, onChanged, exposeSave }) {
  const bandId = band?.id ?? band?.band_id ?? null;

  // Google Maps ladataan Appissa <LoadScript />:illa; riittää että tarkistetaan saatavuus
  const mapsLoaded = typeof window !== "undefined" && !!window.google;


  const [monthDate, setMonthDate] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d;
  });

  // vihreät (open), sekä "busy" = booked/pending/blackout
  const [openDays, setOpenDays] = useState({});
  const [busyDays, setBusyDays] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(() => new Set());
  const [modal, setModal] = useState({ show:false, data:null, loading:false, error:"" });

  // --- Esiintyjäkohtainen puskuri (pv) + UI ---
  const initialBuffer = useMemo(() => {
    const v = Number(
      band?.buffer_days ??
      band?.min_notice_days ??
      band?.settings?.buffer_days ?? 0
    );
    return Number.isFinite(v) && v >= 0 ? v : 0;
  }, [band]);

  const [bufferDays, setBufferDays]   = useState(initialBuffer);
  useEffect(() => { setBufferDays(initialBuffer); }, [initialBuffer]);

  // Onko puskurissa tallentamattomia muutoksia?
  const bufferDirty = useMemo(
    () => bufferDays !== initialBuffer,
    [bufferDays, initialBuffer]
  );

  const saveBuffer = useCallback(async () => {
    if (!bandId) return;
    const v = Math.max(0, Math.min(60, Number(bufferDays) || 0));
    setBufferDays(v);
    try {
    const res = await api.updateBand(bandId, { buffer_days: v });
    const srv = Number(res?.band?.buffer_days);
    const finalV = Number.isFinite(srv) ? srv : v;
    setBufferDays(finalV);
    // 🔔 Päivitä myös vanhempi (Dashboard) – näin initialBuffer vastaa uutta arvoa,
    // kun palaat tabiin eikä näkymä "pomppaa" vanhaan
    onChanged?.({ buffer_days: finalV });
    toast.success("Kalenterin tiedot tallennettu.", { containerId: "global" });
    } catch (e) {
      console.error("save buffer fail", e);
      toast.error("Puskurin tallennus epäonnistui.", { containerId: "global" });
      setBufferDays(initialBuffer);
    }
  }, [bandId, bufferDays, initialBuffer, onChanged]);

  const saveAll = useCallback(async () => {
    if (!bandId) return;
    const dirty = bufferDays !== initialBuffer;
    if (dirty) {
      await saveBuffer();      // kutsuu onChanged?.() sisäisesti
    } else {
      toast.info("Ei muutoksia tallennettavaksi.", { containerId: "global" });
    }
  }, [bandId, bufferDays, initialBuffer, saveBuffer]);

  // Ilmoita ylös sticky-palkille koska “Tallenna” näytetään vain, jos jotain tallennettavaa on
  useEffect(() => {
    if (!exposeSave) return;
    exposeSave(bufferDirty ? saveAll : null);
  }, [exposeSave, bufferDirty, saveAll]);


  // Tänään & puskurin cutoff
  const todayISO = useMemo(() => { const t=new Date(); t.setHours(0,0,0,0); return ymd(t); }, []);
  const cutoffISO = useMemo(() => {
    const t = new Date(); t.setHours(0,0,0,0);
    const c = new Date(t); c.setDate(c.getDate() + (Number(bufferDays) || 0));
    return ymd(c);
  }, [bufferDays]);

  // Ensimmäinen sallittu päivä: jos puskuri > 0, niin cutoff + 1, muuten tänään.
  const firstAllowedISO = useMemo(() => {
    const base = new Date(`${cutoffISO}T00:00:00`);
    if (Number(bufferDays) > 0) base.setDate(base.getDate() + 1);
    return ymd(base);
  }, [cutoffISO, bufferDays]);

  // (pidetään vanhat nimimuuttujat UI-tekstiä varten)
  const cutoffFiShort = useMemo(() => formatFiShort(firstAllowedISO), [firstAllowedISO]);
  const cutoffWeekday = useMemo(() => {
    const d = new Date(`${firstAllowedISO}T00:00:00`);
    return d.toLocaleDateString("fi-FI", { weekday: "long" });
  }, [firstAllowedISO]);

  const monthGrid = useCallback((anchor) => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const jsDow = first.getDay();
    const fiDow = (jsDow + 6) % 7; // ma=0
    const start = addDays(first, -fiDow);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, []);

  const fetchAvailability = useCallback(async (bId, fromISO, toISO, signal) => {
    if (!bId) return {};
   const key = `${bId}|A|${fromISO}|${toISO}`;
   const hit = AV_CACHE.get(key);
   if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.data;

    const res = await api.availability.list(bId, { from: fromISO, to: toISO, signal });
    const obj = (res && typeof res.availability === "object") ? res.availability : {};
    const map = {};
    for (const [d, v] of Object.entries(obj)) {
      const open = v === true || v === 1 || v === "1" || String(v).toLowerCase() === "open";
      if (open) map[d] = true;
    }
   AV_CACHE.set(key, { ts: Date.now(), data: map });
    return map;
  }, []);

  const fetchBusy = useCallback(async (bId, fromISO, toISO, signal) => {
    if (!bId || typeof api.calendar !== "function") return {};
     const key = `${bId}|C|${fromISO}|${toISO}`;
   const hit = CAL_CACHE.get(key);
   if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.data;
    const data = await api.calendar(bId, { from: fromISO, to: toISO, signal });
    const src = Array.isArray(data?.events) ? data.events : [];
    const TTL_MS = Number(holdTtlHours) * 60 * 60 * 1000;
    const now = Date.now();
    const out = {};

    for (const ev of src) {
      const xp = ev.extendedProps || {};
      const st = (xp.status || ev.status || "").toLowerCase();
      if (!st) continue;

      if (st === "pending") {
        const exp  = xp.expires_at ? new Date(xp.expires_at).getTime() : null;
        const sent = xp.sent_at    ? new Date(xp.sent_at).getTime()    : null;
        const alive = exp ? exp > now : sent ? sent + TTL_MS > now : true;
        if (!alive) continue;
      }

      const sStr = (ev.start || ev.startStr || "").slice(0, 10);
      const eStr = (ev.end   || ev.endStr   || "").slice(0, 10) || sStr;
      if (!sStr) continue;

      const start = new Date(`${sStr}T00:00:00`);
      const endEx = new Date(`${eStr}T00:00:00`);
      const end = isNaN(endEx) || eStr === sStr ? addDays(start, 1) : endEx;

      for (let d=new Date(start); d<end; d=addDays(d,1)) {
        const key = ymd(d);
        const cur = out[key]?.status;
        // "booked" ja "completed" voittavat kaiken; "pending" ei yliaja varattua;
        if (cur === "booked" || cur === "completed") continue;
        if (
          st === "booked" ||
          st === "completed" ||
          (st === "pending" && cur !== "booked" && cur !== "completed") ||
          (st === "blackout" && !cur)
        ) {
          out[key] = { status: st, id: ev.id, extendedProps: xp };
        }
      }
    }
   CAL_CACHE.set(key, { ts: Date.now(), data: out });
   return out;
  }, [holdTtlHours]);

    const prefetchMonth = useCallback(async (anchor) => {
    if (!bandId) return;
    const days = monthGrid(anchor);
    const fromISO = ymd(days[0]);
    const toISOExclusive = ymd(addDays(days[days.length - 1], 1));
    try {
      await Promise.all([
        fetchAvailability(bandId, fromISO, toISOExclusive, undefined),
        fetchBusy(bandId, fromISO, toISOExclusive, undefined),
      ]);
    } catch {} // hiljaa – esilataus saa epäonnistua
  }, [bandId, monthGrid, fetchAvailability, fetchBusy]);

  useEffect(() => {
   prefetchMonth(addMonths(monthDate, +1));
    prefetchMonth(addMonths(monthDate, -1));
  }, [monthDate, prefetchMonth]);

  // Rinnakkaisuus bulk-fallbackille
  const BULK_CONCURRENCY = 16;

  // Kuukauden massamuutos: makeOpen=true → kaikki vihreiksi, false → kaikki harmaiksi.
  // Skippaa puskurissa olevat sekä varatut/pending/blackout -päivät.
  // Käyttää maltillista rinnakkaisuutta, ettei backend tukkeudu.
  const bulkToggleMonth = useCallback(async (makeOpen) => {
    if (!bandId) return;

    // 1) Kuukauden kaikki ruudukon päivät → suodata vain kuluvan kuukauden päivät
    const grid = monthGrid(monthDate);
    const monthIsos = grid
      .filter((d) => sameMonth(d, monthDate))
      .map((d) => ymd(d));

    // 2) Päivät, joita SAA muuttaa (ei puskuriin osuvia, ei busy-statusta)
    const eligible = monthIsos.filter((iso) => {
      const inBuffer =
        iso < cutoffISO || (Number(bufferDays) > 0 && iso === cutoffISO);
      const st = busyDays[iso]?.status; // booked | pending | blackout | undefined
      const locked = st === "booked" || st === "pending" || st === "blackout" || st === "completed";
      return !inBuffer && !locked;
    });

    // 3) Delta: vain ne, joiden tila oikeasti muuttuisi
    const delta = eligible.filter((iso) => (!!openDays[iso]) !== !!makeOpen);
  // Jos mikään päivä ei muuttuisi, ei tehdä mitään
  if (delta.length === 0) {
    toast.info("Ei muokattavia päiviä tässä kuussa.", { containerId: "global" });
    return;
  }

    // 4) Optimistinen paikallinen tila (vain delta)
    setOpenDays((prev) => {
      const next = { ...prev };
      
      if (makeOpen) for (const iso of delta) next[iso] = true;
      else for (const iso of delta) delete next[iso];
      return next;
    });
    setSaving((s) => {
      const n = new Set(s);
      for (const iso of delta) n.add(iso);
      return n;
    });

    // 5) Lähetä palvelimelle: suosi bulk-endpointtia jos saatavilla, muuten fallback
    let failed = 0;
    if (typeof api.availability?.bulkSet === "function") {
      // chunkkaa esim. 100 päivää / pyyntö
      for (let i = 0; i < delta.length; i += 100) {
        const chunk = delta.slice(i, i + 100);
        try {
          await api.availability.bulkSet(bandId, { dates: chunk, open: !!makeOpen });
        } catch {
          failed += chunk.length;
          // revertoi chunk optimistisesti
          setOpenDays((prev) => {
            const n = { ...prev };
            if (makeOpen) for (const iso of chunk) delete n[iso];
            else for (const iso of chunk) n[iso] = true;
            return n;
          });
        } finally {
          setSaving((s) => {
            const n = new Set(s);
            for (const iso of chunk) n.delete(iso);
            return n;
          });
        }
      }
     } else {
      // Fallback: päiväkohtaiset kutsut rajoitetulla rinnakkaisuudella
      for (let i = 0; i < delta.length; i += BULK_CONCURRENCY) {
        const chunk = delta.slice(i, i + BULK_CONCURRENCY);
        const results = await Promise.all(
          chunk.map(async (iso) => {
            try {
              await saveDay(bandId, iso, makeOpen);
              return true;   // OK
            } catch {
              // revert paikallisesti
              setOpenDays((prev) => {
                const n = { ...prev };
                if (makeOpen) delete n[iso];
                else n[iso] = true;
                return n;
              });
              return false;  // FAIL
            } finally {
              setSaving((s) => {
                const n = new Set(s);
                n.delete(iso);
                return n;
              });
            }
          })
        );
        // laske epäonnistumiset mapin tuloksista (ei sisällä funktiossa)
        failed += results.filter((ok) => !ok).length;
      }
    }

    // 6) Notifikaatio + ilmoitus parentille
    const ok = delta.length - failed;
    if (failed === 0) {
      toast.success(
        makeOpen
          ? `Avattiin ${ok} päivää buukattaviksi.`
          : `Suljettiin ${ok} päivää buukkaukselta.`,
        { containerId: "global" }
      );
    } else {
      toast.error(
        makeOpen
          ? `Avattiin ${ok} päivää, ${failed} epäonnistui.`
          : `Suljettiin ${ok} päivää, ${failed} epäonnistui.`,
        { containerId: "global" }
      );
    }
    onChanged?.(); // esim. checklistin refetch
  }, [
    bandId,
    monthDate,
    monthGrid,
    cutoffISO,
    bufferDays,
    busyDays,
    openDays,
    onChanged,
  ]);


  async function saveDay(bId, dayISO, makeOpen) {
    if (!bId) return false;
    await api.availability.set(bId, dayISO, makeOpen);
    return true;
  }

  useEffect(() => {
    const ab = new AbortController();
    (async () => {
      if (!bandId) {
        setOpenDays({}); setBusyDays({}); setLoading(false); return;
      }
      setLoading(true);
      try {
        const days = monthGrid(monthDate);
        const fromISO = ymd(days[0]);
        const toISOExclusive = ymd(addDays(days[days.length - 1], 1));
        const [openMap, busyMap] = await Promise.all([
          fetchAvailability(bandId, fromISO, toISOExclusive, ab.signal),
          fetchBusy(bandId, fromISO, toISOExclusive, ab.signal),
        ]);
        setOpenDays(openMap); setBusyDays(busyMap);
      } catch (e) {
        if (e?.name !== "AbortError") console.error("calendar load fail", e);
      } finally {
        setLoading(false);
      }
    })();
    return () => ab.abort();
  }, [bandId, monthDate, monthGrid, fetchAvailability, fetchBusy]);

  // Päiväklikkaus
  const onDayClick = async (d) => {
    if (!bandId) return;
    const dISO = ymd(d);
    if (saving.has(dISO)) return;
    // mennyt päivä tai puskurissa; BE estää myös cutoff-päivän jos puskuri > 0
    const inBuffer =
      dISO < cutoffISO ||
      (Number(bufferDays) > 0 && dISO === cutoffISO);
    if (inBuffer) return;

    const status = busyDays[dISO]?.status; // booked | pending | blackout | undefined

    if (isDashboard && (status === "booked" || status === "pending")) {
      // Fallback-arvot suoraan kalenterin extendedPropsista (näytetään heti)
      const xp = busyDays[dISO]?.extendedProps || {};
      // yritetään useita nimiä, jos BE käyttää eri avainta
      const initialVenue   = xp.venue_name || xp.venue || xp.event_location ||
                             xp.location || xp.venue_address || "";
      const initialCust    = xp.customer_name || "";
      const initialPhone   = xp.customer_phone || "";
      const initialPrice   = xp.base_price_eur ?? xp.price_eur ?? null;
      const initialKmRate  = xp.km_price_eur ?? null;
      const initialDistKm  = xp.distance_km ?? null;
      const initialDistTxt = xp.distance_text || "";
      const initialTotal   = xp.total_eur ?? null;
      const initialTravel  = xp.travel_cost_eur ?? null;

      setModal({
        show: true,
        data: {
          date: dISO,
          status,
          customer_name: initialCust,
          customer_phone: initialPhone,
          venue_name: initialVenue,
          price_eur: initialPrice,
          price_text: xp.price_text || "",
          base_price_eur: xp.base_price_eur ?? null,
          km_price_eur: initialKmRate,
          distance_km: initialDistKm,
          distance_text: initialDistTxt,
          total_eur: initialTotal,
          travel_cost_eur: initialTravel,
        },
        loading: true,
        error: "",
      });


        try {
        const det = await api.bookings.ownerDay(bandId, dISO);
        setModal((m) => {
          const prevStatus = (m.data?.status || "").toLowerCase();

          // Älä yliaja jo näytettyjä arvoja tyhjillä (null/undefined/"" )
          const safeDet = Object.fromEntries(
            Object.entries(det || {}).filter(([_, v]) => {
              if (v === null || v === undefined) return false;
              if (typeof v === "string" && v.trim() === "") return false;
              return true;
            })
          );
          // Jos BE käyttää event_locationia, mapataan se venue_nameksi
          if (safeDet.event_location && !safeDet.venue_name) {
            safeDet.venue_name = safeDet.event_location;
          }

          const merged = { ...m.data, ...safeDet };
          // Älä koskaan “downgreadaa” openiksi
          if (prevStatus && (merged.status || "").toLowerCase() === "open") {
            merged.status = m.data.status;
          }
          return { ...m, loading: false, data: merged, error: det ? "" : "Tietojen haku epäonnistui" };
        });
      } catch (e) {
        console.error("ownerDay fetch failed", e);
        setModal((m) => ({ ...m, loading: false, error: "Tietojen haku epäonnistui" }));
      }
      return;
    }

    if (status === "booked" || status === "pending" || status === "blackout") {
      toast.info("Päivää ei voi muuttaa: varattu / tarjous vireillä / estetty.");
      return;
    }

    const willOpen = !openDays[dISO];
    setOpenDays((m) => { const c = { ...m }; if (willOpen) c[dISO] = true; else delete c[dISO]; return c; });
    setSaving((s) => new Set(s).add(dISO));

    try {
      await saveDay(bandId, dISO, willOpen);
      // 🔔 ilmoita ylös: checklist refetch
      onChanged?.();
    } catch (e) {
      setOpenDays((m) => { const c = { ...m }; if (!willOpen) c[dISO] = true; else delete c[dISO]; return c; });
      console.error("save day fail", e);
      toast.error("Päivän tallennus epäonnistui.", { containerId: "global" });
    } finally {
      setSaving((s) => { const n = new Set(s); n.delete(dISO); return n; });
    }
  };

  // --- UI ---
  const days = monthGrid(monthDate);

  const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const nowStart   = new Date(); nowStart.setDate(1); nowStart.setHours(0,0,0,0);
  const canGoPrev  = monthStart > nowStart;

  const goPrev  = () => canGoPrev && setMonthDate((d)=>addMonths(d,-1));
  const goNext  = () => setMonthDate((d)=>addMonths(d,+1));
  const goToday = () => setMonthDate(new Date(new Date().setDate(1)));

  // ---------- Matkalaskenta modalissa ----------
  const [calc, setCalc] = useState({ distanceKm: null, distanceText: null, travelCost: null, total: null, loading: false });

  useEffect(() => {
    if (!modal.show || modal.loading) return;

    const basePrice =
      modal.data?.base_price_eur != null
        ? Number(modal.data.base_price_eur)
        : (modal.data?.price_eur != null ? Number(modal.data.price_eur) : null);
    const kmRate = Number(modal.data?.km_price_eur ?? 0);

    // Jos BE antoi distance_km, käytä sitä ja kerro 2 (edestakainen)
    if (modal.data?.distance_km != null) {
      const dk1 = Number(modal.data.distance_km);
      const dk  = Number.isFinite(dk1) ? dk1 * 2 : null;
      const travel = dk != null && kmRate > 0 ? Math.round(dk * kmRate * 100) / 100 : null;
      const tot    = basePrice != null ? Math.round(((basePrice || 0) + (travel || 0)) * 100) / 100 : null;
      setCalc({ distanceKm: dk, distanceText: null, travelCost: travel, total: tot, loading: false });
      return;
    }

    // Jos BE antoi valmiiksi matkan tai matkakulut, käytä niitä.
    const hasServerDistance = modal.data?.distance_km != null;
    const hasServerTravel   = modal.data?.travel_cost_eur != null;
    const hasServerTotal    = modal.data?.total_eur != null;
    if (hasServerDistance || hasServerTravel) {
      const dk = hasServerDistance ? Number(modal.data.distance_km) : null;
      const travel = hasServerTravel
        ? Number(modal.data.travel_cost_eur)
        : (dk != null && kmRate > 0 ? Math.round(dk * kmRate * 100) / 100 : null);
      const tot = hasServerTotal
        ? Number(modal.data.total_eur)
        : (basePrice != null ? Math.round(((basePrice || 0) + (travel || 0)) * 100) / 100 : null);
      setCalc({
        distanceKm: dk,
        distanceText: modal.data?.distance_text || (dk != null ? `${dk.toFixed(1)} km` : null),
        travelCost: travel,
        total: tot,
        loading: false,
      });
      return;
    }

    // Muutoin yritä laskea Googlelta
    const origin = (band?.band_address || "").trim();
    const dest = (modal.data?.venue_name || "").trim();
    if (!origin || !dest || !mapsLoaded || typeof window === "undefined" || !window.google) {
      const tot = basePrice != null ? Math.round((basePrice || 0) * 100) / 100 : null;
      setCalc({ distanceKm: null, distanceText: null, travelCost: null, total: tot, loading: false });
      return;
    }

    setCalc((c) => ({ ...c, loading: true }));
    const svc = new window.google.maps.DistanceMatrixService();
    svc.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [dest],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (res, status) => {
        try {
          if (status !== "OK") {
            const tot = basePrice != null ? Math.round((basePrice || 0) * 100) / 100 : null;
            setCalc({ distanceKm: null, distanceText: null, travelCost: null, total: tot, loading: false });
            return;
          }
          const el = res?.rows?.[0]?.elements?.[0];
          if (!el || el.status !== "OK") {
            const tot = basePrice != null ? Math.round((basePrice || 0) * 100) / 100 : null;
            setCalc({ distanceKm: null, distanceText: null, travelCost: null, total: tot, loading: false });
            return;
          }
          const km1 = (el.distance?.value || 0) / 1000; // yksi suunta
          const km  = km1 * 2;                           // edestakainen
          const travel = kmRate > 0 ? Math.round(km * kmRate * 100) / 100 : null;
          const tot    = basePrice != null ? Math.round(((basePrice || 0) + (travel || 0)) * 100) / 100 : null;
          setCalc({ distanceKm: km, distanceText: null, travelCost: travel, total: tot, loading: false });
        } catch {
          const tot = basePrice != null ? Math.round((basePrice || 0) * 100) / 100 : null;
          setCalc({ distanceKm: null, distanceText: null, travelCost: null, total: tot, loading: false });
        }
      }
    );
  }, [
    modal.show, modal.loading,
    modal.data?.venue_name,
    modal.data?.base_price_eur,
    modal.data?.price_eur,
    modal.data?.km_price_eur,
    modal.data?.distance_km,
    modal.data?.distance_text,
    modal.data?.travel_cost_eur,
    modal.data?.total_eur,
    band?.band_address,
    mapsLoaded
  ]);

  // --- Mitä näytetään modalissa? ---
  const modalStatus = (modal?.data?.status || "").toLowerCase();
  const showPrivate = modalStatus === "booked" || modalStatus === "completed"; // Asiakas + puhelin vain varatuissa
  const showTravel  = modalStatus === "booked";                                // Matkakulut vain varatuissa

  return (
    <div className="space-y-4 p-4 min-w-0">
      {/* Header: navigaatio + kuukausi + puskuri */}
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between min-w-0">

      </div>

      <div className="rounded-md bg-blue-100/60 dark:bg-blue-900/30 p-3 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">
        Oletuksena kalenteri on kiinni. Avaa buukattavat päivät klikkaamalla ruutuja.
        Puskuri määrittää, millä aikataululla olet valmis esiintymään. Esimerkiksi jos puskuri on 7 päivää ja tänään on lauantai, näyt etusivun haussa aikaisintaan ensi viikon lauantaina – kunhan päivä on merkitty vihreäksi (buukattavissa). Ohjeteksti kertoo tämän tarkasti.
        {bufferDays > 0 ? (
          <></>
        ) : (
          <> Jos päivä on merkitty vapaaksi, sinut voi buukata myös <b>tänään</b>.</>
        )}
        {!isDashboard && " Keikkojen yksityiskohdat näkyvät vain esiintyjän hallintapaneelissa."}
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap items-center gap-3 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm pb-4">
        <span className="inline-flex items-center gap-1">
          <span className={`inline-block w-3 h-3 rounded ${CAL_THEME.legendOpen}`} />
          Buukattavissa
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-yellow-400/30 ring-1 ring-yellow-600/40" />
          Tarjous vireillä
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-red-500/30 ring-1 ring-red-600/40" />
          Varattu
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-black/10 ring-1 ring-black/10 dark:bg-white/10 dark:ring-white/10" />
          Ei varattavissa
        </span>
      </div>

              {/* Puskuri (pv) – vähimmäisilmoitusaika */}
        <div className="order-3 w-full md:w-auto flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
            <span
              className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm uppercase tracking-wide opacity-80 shrink-0"
              title="Puskuri = vähimmäisilmoitusaika. Profiilisi nousee hakuun vasta puskurin jälkeen, kun päivä on avattu vihreäksi."
            >
              Puskuri (PV)
            </span>

            <div className="flex items-center gap-2 rounded-full px-2 py-1 bg-gradient-to-r from-fuchsia-700/40 to-sky-700/40 ring-1 ring-white/10 shadow w-full sm:w-auto max-w-full">
              <button
                type="button"
                className="px-2 py-1 rounded-full bg-black/30 hover:bg-black/40 text-white/90 shrink-0"
                onClick={() => setBufferDays(n => Math.max(0, (Number(n)||0) - 1))}
                aria-label="Vähennä puskuria"
              >–</button>

              <input
                type="number"
                min={0}
                max={60}
                inputMode="numeric"
                value={bufferDays}
                onChange={(e) => setBufferDays(Math.max(0, Math.min(60, Number(e.target.value)||0)))}
                className="w-full max-w-[64px] text-center bg-transparent outline-none text-white font-semibold"
                aria-label="Vähimmäisilmoitusaika päivinä"
              />

              <button
                type="button"
                className="px-2 py-1 rounded-full bg-black/30 hover:bg-black/40 text-white/90 shrink-0"
                onClick={() => setBufferDays(n => Math.min(60, (Number(n)||0) + 1))}
                aria-label="Lisää puskuria"
              >+</button>
            </div>
          </div>

          {/* Selite */}
          <span className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm pb-4 opacity-80 sm:ml-0" aria-live="polite">
            {bufferDays > 0 ? (
              <>
                Profiilisi näkyy haussa aikaisintaan <b>{cutoffFiShort}</b> ({cutoffWeekday}),
                kun päivä on <b>avattu vihreäksi</b>. Puskurin ( {bufferDays} pv ) sisällä olevat
                päivät eivät näy haussa eikä niitä voi avata buukattavaksi.
              </>
            ) : (
              <>
                Profiilisi näkyy haussa myös <b>tänään</b>, jos päivä on avattu vihreäksi.
              </>
            )}
          </span>
        </div>

        {/* Navigaatio */}
        <div className="flex items-center gap-1 order-1">
          <NeonButton size="sm" onClick={goPrev} disabled={!canGoPrev}>←</NeonButton>
          <NeonButton size="sm" 
          onClick={goToday}>TÄNÄÄN</NeonButton>
          <NeonButton size="sm" onClick={goNext}>→</NeonButton>
        </div>

       {/* Kuukauden massatoiminnot */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 order-1 w-full">
          <NeonButton
            size="sm"
            variant="secondary"
            className="w-full md:w-auto md:min-w-[220px] md:flex-none scale-[0.80] md:scale-100"
            onClick={() => bulkToggleMonth(true)}
            disabled={!bandId || loading}
            title="Avaa kaikki kuluvan kuukauden päivät buukattaviksi (ohittaa puskuri- ja varauspäivät)"
          >
            Avaa kaikki päivät
          </NeonButton>
          <NeonButton
            size="sm"
            className="w-full md:w-auto md:min-w-[220px] md:flex-none scale-[0.80] md:scale-100"
            onClick={() => bulkToggleMonth(false)}
            disabled={!bandId || loading}
            title="Sulje kaikki kuluvan kuukauden päivät (ohittaa puskuri- ja varauspäivät)"
          >
            Sulje kaikki päivät
          </NeonButton>
        </div>

        {/* Kuukausiotsikko */}
        <div className="order-2 text-lg sm:text-base font-semibold dark:text-white/90 text-black/90">
          {monthDate.toLocaleDateString("fi-FI", { month: "long", year: "numeric" }).toUpperCase()}
        </div>

      {/* Kalenteri */}
      <div className=" rounded-lg ring-1 ring-black/10 dark:ring-white/50 overflow-hidden w-full max-w-[980px] mx-auto">

      
        <div className="grid grid-cols-7 text-center text-[11px] md:text-[10px] bg-black/5 dark:bg-white/50 py-1.5 md:py-1">

        
          {["Ma","Ti","Ke","To","Pe","La","Su"].map((w)=>(
            <div key={w} className="font-medium">{w}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map((d) => {
            const dISO     = ymd(d);
            const dLabel   = formatFiShort(dISO);
            const inMonth  = sameMonth(d, monthDate);
            const isToday  = dISO === todayISO;
            const isSaving = saving.has(dISO);
            // näkyvyys/klikkaus sama linja kuin BE: cutoff-päivä on vielä blokissa jos puskuri > 0
            const inBuffer =
              dISO < cutoffISO ||
              (Number(bufferDays) > 0 && dISO === cutoffISO);
            const isOpen   = !!openDays[dISO];
            const st       = busyDays[dISO]?.status;

            const base = "relative border border-black/5 dark:border-white/30 transition text-xs sm:text-[11px]";
            let bg = CAL_THEME.closedHover;
            if (st === "booked")  bg = CAL_THEME.bookedBg;
            else if (st === "pending")  bg = CAL_THEME.pendingBg;
            else if (st === "blackout") bg = CAL_THEME.blackoutBg;
            else if (isOpen)      bg = `${CAL_THEME.openBg} ring-3 ${CAL_THEME.openRing}`;
            const dim = inMonth ? "" : CAL_THEME.pastDim;

            return (
              <button
                key={dISO}
                type="button"
                className={[
                  base,
                  "w-full aspect-square min-h-[44px] sm:min-h-[40px] md:min-h-[36px]",
                  bg,
                  dim,
                  inBuffer ? "cursor-not-allowed opacity-50" : ""
                ].join(" ")}
                onClick={() => onDayClick(d)}
                disabled={!inMonth || loading || inBuffer}
                aria-pressed={isOpen}
                aria-label={`${dLabel} ${st ? st : (isOpen ? "buukattavissa" : "ei varattavissa")}`}
                title={
                  st === "booked"   ? `Varattu – ${dLabel}` :
                  st === "pending"  ? `Tarjous vireillä – ${dLabel}` :
                  st === "blackout" ? `Ei varattavissa – ${dLabel}` :
                  inBuffer          ? `Puskurissa (${bufferDays} pv) – ${dLabel}` :
                  isOpen            ? `Buukattavissa – ${dLabel}` :
                                      `Suljettu – ${dLabel}`
                }
              >
                <span className="absolute top-1 left-1 text-[12px] md:text-[14px] mdd:text-[14px] lg:text-[14px] xl:text-[14px] 2xl:text-[14px] opacity-100 select-none dark:text-white">
                  {d.getDate()}
                </span>

                {isToday && (
                  <span className={`absolute left-2 text-[9px] md:text-[14px] px-1 rounded ${CAL_THEME.todayBadge}`}>
                    tänään
                  </span>
                )}

                {isSaving && (
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="text-[10px] opacity-80">Tallennetaan…</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <p className="text-sm opacity-75">Ladataan saatavuuksia…</p>}

      {/* Modal vain dashboardiin */}
      {isDashboard && modal.show && (
        <div
          className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={(e)=>{ if (e.target===e.currentTarget) setModal({ show:false, data:null, loading:false, error:"" }); }}
        >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md max-h-[85vh] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] rounded-xl bg-white dark:bg-night-800 text-black dark:text-white shadow-xl"
            >

            <div className="px-5 py-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
              <h3 className="font-customFont2 dark:text-white/90 text-black/90 text-lg xxxsm:text-sm xxsm:text-sm s24:text-sm xsm:text-sm sm:text-lg md:text-lg mdd:text-lg font-semibold">Kalenteritapahtuman tiedot</h3>
              <button
                type="button"
                className="rounded px-2 py-1 text-sm bg-black/5 dark:bg-white/10"
                onClick={()=>setModal({ show:false, data:null, loading:false, error:"" })}
              >
                Sulje
              </button>
            </div>

            <div className="px-5 py-4 space-y-2">
              {modal.loading ? (
                <div className="font-customFont2 dark:text-white/90 text-black/90 text-lg font-semibold">Ladataan tietoja…</div>
              ) : (
                <>
                  <Row label="Päivä" value={modal.data?.date ? formatFiLong(modal.data.date) : "–"} />

                  <Row
                    label="Tila"
                    value={
                      modal.data?.status === "booked"
                        ? "Varattu"
                        : modal.data?.status === "pending"
                          ? "Tarjous vireillä"
                          : "–"
                    }
                  />

                  {/* Asiakas- ja puhelintiedot vain varatuissa */}
                  {showPrivate && <Row label="Asiakas" value={modal.data?.customer_name || "–"} />}
                  {showPrivate && <Row label="Puhelin" value={modal.data?.customer_phone || "–"} />}

                  {/* Venue näytetään aina (venue_name tai event_location) */}
                  <Row label="Venue" value={modal.data?.venue_name || modal.data?.event_location || "–"} />

                  {/* Hinta (pohjahinta, ilman matkakuluja) */}
                  <Row
                    label="Hinta"
                    value={
                      modal.data?.base_price_eur != null
                        ? EUR(modal.data.base_price_eur)
                        : modal.data?.price_eur != null
                          ? EUR(modal.data.price_eur)
                          : (modal.data?.price_text || "–")
                    }
                  />

                  {/* Matkakulut vain varatuissa */}
                  {showTravel && (
                    <Row
                      label="Matkakulut edestakaisin"
                      value={calc.loading ? "Lasketaan…" : (calc.travelCost != null ? EUR(calc.travelCost) : "–")}
                    />
                  )}

                  {/* Yhteensä vain varatuissa */}
                  {modal.data?.status === "booked" && (
                    <Row label="Yhteensä" value={calc.total != null ? EUR(calc.total) : "–"} />
                  )}

                  {modal.error && <div className="text-xs text-rose-500">{modal.error}</div>}
                </>
              )}
            </div>

          <div className="px-5 py-3 border-t border-black/10 dark:border-white/10 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
            {(modalStatus === "booked" || modalStatus === "pending") && (
              <>
                <NeonButton
                  size="sm"
                  IconLeft={CalendarPlus}
                onClick={() => {
                    const venue  = modal.data?.venue_name || modal.data?.event_location || "Tapahtumapaikka";
                    const artist = band?.name || "Esiintyjä";
                    const title  = `${artist} keikka ${venue}`;

                    const details = [
                      "Esiintyminen buukattu Buukkeri-palvelun kautta.",
                      fiStatusLine(modal, holdTtlHours),
                      showPrivate && modal.data?.customer_name  ? `Asiakas: ${modal.data.customer_name}`  : "",
                      showPrivate && modal.data?.customer_phone ? `Puh: ${modal.data.customer_phone}`     : "",
                    ].filter(Boolean).join("\n");

                    const gUrl = buildGoogleLink({
                      title,
                      date: modal.data?.date,     // YYYY-MM-DD
                      // startTime: "19:00",      // halutessasi kellonajat
                      // endTime:   "22:00",
                      location: venue,
                      details,
                    });

                    window.open(gUrl, "_blank", "noopener,noreferrer");
                  }}
                >
                  Lisää Google-kalenteriin
                </NeonButton>

                <NeonButton
                  size="sm"
                  variant="secondary"
                  IconLeft={Download}
                  onClick={() => {
                    const venue  = modal.data?.venue_name || modal.data?.event_location || "Tapahtumapaikka";
                    const artist = band?.name || "Esiintyjä";
                    const title  = `${artist} keikka ${venue}`;

                    const description = [
                      "Esiintyminen buukattu Buukkeri-palvelun kautta.",
                      fiStatusLine(modal, holdTtlHours),
                      showPrivate && modal.data?.customer_name  ? `Asiakas: ${modal.data.customer_name}`  : "",
                      showPrivate && modal.data?.customer_phone ? `Puh: ${modal.data.customer_phone}`     : "",
                    ].filter(Boolean).join("\n");

                    const ics = buildICS({
                      uid: `band-${band?.id || band?.band_id}-${modal.data?.date}@buukkeri`,
                      title,
                      date: modal.data?.date,     // YYYY-MM-DD
                      // startTime: "19:00",
                      // endTime:   "22:00",
                      location: venue,
                      description,
                    });

                    const fname = `keikka-${(artist || "esiintyja").toLowerCase().replace(/[^a-z0-9]+/gi, "-")}-${modal.data?.date}.ics`;
                    downloadICS(fname, ics);
                  }}
                >
                  Lataa .ics
                </NeonButton>
              </>
            )}

            <button
              type="button"
              className="rounded px-3 py-2 bg-black/80 text-white dark:bg-white/20"
              onClick={() => setModal({ show:false, data:null, loading:false, error:"" })}
            >
              OK
            </button>
          </div>

          </div>
        </div>
      )}
    </div>
  );
}

CalendarTab.propTypes = {
  band: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    band_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    buffer_days: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    min_notice_days: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // legacy
    settings: PropTypes.object,
    band_address: PropTypes.string, // ⬅️ käytetään matkalaskentaan
  }),
  isDashboard: PropTypes.bool,
  holdTtlHours: PropTypes.number,
  onChanged: PropTypes.func,   // 🔔 checklistin päivitys
  exposeSave: PropTypes.func,  // ⬅️ sticky “Tallenna” tätä varten
};
